import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { CloseCharge } from "@/components/wo/CloseCharge";

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const LOCKED = ["no_aplica", "por_configurar"];
const EMPTY_MAINT = { next_date: null, periodicity_months: null, recommendation: null, reason: null };
const policyMaint = (prev) => {
  const { policy: _p, mode: _m, ...vals } = prev?.maintenance || {};
  return vals;
};

const LockedInput = ({ testid }) => (
  <Input type="text" disabled readOnly value="" placeholder="Sin dato" tabIndex={-1}
    className="border-dashed bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60" data-testid={testid} />
);

export const CloseDialog = ({ wo, open, onOpenChange, onDone }) => {
  const NO_SIG_REASONS = ["Cliente no disponible para firma.", "Cliente rechazó firmar.", "Imposibilidad de obtener firma.", "Servicio realizado con autorización del responsable.", "Otro"];
  const [prev, setPrev] = useState(null);
  const [w, setW] = useState({});
  const [m, setM] = useState({});
  const [justification, setJustification] = useState("");
  const [noSig, setNoSig] = useState("");
  const [review, setReview] = useState("");
  const [busy, setBusy] = useState(false);
  const [charge, setCharge] = useState(null);
  const [extraCharges, setExtraCharges] = useState({});

  useEffect(() => {
    if (!open) return;
    api.get(`/work-orders/${wo.id}/close-preview`).then(({ data }) => {
      setPrev(data);
      const { policy: _wp, ...wr } = data.warranty;
      const { policy: _mp, ...mr } = data.maintenance;
      setW(wr);
      setM(mr);
      const cc = data.client_charge;
      setCharge({ chargeable: cc.chargeable, service_amount: cc.service_amount, extras: cc.extras || [], notes: cc.notes || "" });
      setExtraCharges(Object.fromEntries((data.extra_charges || []).map((x) => [x.service_id, { chargeable: x.chargeable, service_amount: x.service_amount, extras: x.extras || [], notes: x.notes || "" }])));
    });
  }, [open, wo.id]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/work-orders/${wo.id}/close`, { warranty: w, maintenance: m, justification: justification || null, review_notes: review, no_signature_reason: wo.signature ? null : noSig.trim() || null,
        client_charge: prev.client_charge.direct ? charge : null,
        extra_charges: Object.fromEntries((prev.extra_charges || []).filter((x) => x.direct).map((x) => [x.service_id, extraCharges[x.service_id]])) });
      toast.success(data.pdf ? "OS cerrada y PDF generado" : "OS cerrada (el PDF se generará al descargarlo)");
      onOpenChange(false);
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setWf = (k) => (e) => setW((s) => ({ ...s, [k]: e.target.value || null }));
  const setMf = (k) => (e) => setM((s) => ({ ...s, [k]: k === "periodicity_months" ? (e.target.value ? Number(e.target.value) : null) : e.target.value || null }));
  const locked = LOCKED.includes(m.mode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="close-dialog">
        <DialogHeader><DialogTitle className="font-display">Cerrar OS {wo.folio}</DialogTitle></DialogHeader>
        {!prev ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-primary" /> : (
          <div className="space-y-5">
            {!wo.signature && (
              <div className="space-y-3 rounded-lg border border-red-500/40 bg-red-500/10 p-4" data-testid="close-no-signature-block">
                <div className="font-mono text-[11px] uppercase tracking-wider text-red-300">Cierre administrativo sin firma</div>
                <p className="text-sm text-red-100/80">La OS no tiene firma del cliente. Puedes solicitarla por enlace o cerrar indicando el motivo. Quedará como "Cerrada sin firma del cliente".</p>
                <div className="flex flex-wrap gap-2">
                  {NO_SIG_REASONS.map((r) => <button key={r} type="button" onClick={() => setNoSig(r)} className={`rounded-md border px-2 py-1 text-xs ${noSig === r ? "border-red-400 bg-red-500/20" : "border-border"}`} data-testid={`close-nosig-preset-${r.slice(0, 10).replace(/\W+/g, "-").toLowerCase()}`}>{r}</button>)}
                </div>
                <F label="Motivo del cierre *"><Textarea value={noSig} onChange={(e) => setNoSig(e.target.value)} rows={2} data-testid="close-no-signature-reason" /></F>
              </div>
            )}
            {prev.missing.length > 0 && (
              <div className="space-y-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4" data-testid="close-missing-warning">
                <div className="flex items-center gap-2 font-medium text-amber-300"><AlertTriangle className="h-4 w-4" />Elementos obligatorios sin completar</div>
                <ul className="list-disc pl-5 text-sm text-amber-100/80">{prev.missing.map((x) => <li key={x}>{x}</li>)}</ul>
                <F label="Justificación autorizada *"><Textarea value={justification} onChange={(e) => setJustification(e.target.value)} rows={2} data-testid="close-justification-input" /></F>
              </div>
            )}
            <div className="space-y-3">
              <h3 className="font-display font-semibold">Garantía</h3>
              <p className="text-xs text-muted-foreground" data-testid="close-warranty-source">{prev.warranty.source === "reinstalacion" ? "Reinstalación de equipo en resguardo TSSMX: no inicia ni renueva garantía." : prev.warranty.months ? `${prev.warranty.months} meses · ${prev.warranty.source === "seleccion" ? "seleccionada en la OS" : "según política"}` : "Sin garantía (sin política específica ni selección). Captúrala manualmente si aplica."}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <F label="Inicio"><Input type="date" value={w.start_date || ""} onChange={setWf("start_date")} data-testid="close-warranty-start" /></F>
                <F label="Término"><Input type="date" value={w.end_date || ""} onChange={setWf("end_date")} data-testid="close-warranty-end" /></F>
              </div>
              <F label="Condiciones / política"><Textarea rows={2} value={w.conditions || ""} onChange={setWf("conditions")} data-testid="close-warranty-conditions" /></F>
              <F label="Observaciones"><Input value={w.notes || ""} onChange={setWf("notes")} data-testid="close-warranty-notes" /></F>
            </div>
            <div className="space-y-3">
              <h3 className="font-display font-semibold">Mantenimiento</h3>
              <div className="flex flex-wrap gap-2">
                {[["politica", "Según política"], ["no_aplica", "No aplica"], ["por_configurar", "Por configurar"], ["autorizada", "Configuración autorizada"]].map(([v, l]) => (
                  <Button key={v} type="button" size="sm" variant={m.mode === v ? "default" : "outline"} data-testid={`close-maint-mode-${v}`}
                    onClick={() => setM((s) => ({ ...s, ...(LOCKED.includes(v) ? EMPTY_MAINT : v === "politica" ? policyMaint(prev) : {}), mode: v }))}>{l}</Button>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <F label="Próximo mantenimiento">{locked ? <LockedInput testid="close-maint-next" /> : <Input type="date" value={m.next_date || ""} onChange={setMf("next_date")} data-testid="close-maint-next" />}</F>
                <F label="Periodicidad (meses)">{locked ? <LockedInput testid="close-maint-period" /> : <Input type="number" value={m.periodicity_months ?? ""} onChange={setMf("periodicity_months")} data-testid="close-maint-period" />}</F>
              </div>
              <F label="Recomendación">{locked ? <LockedInput testid="close-maint-recommendation" /> : <Input value={m.recommendation || ""} onChange={setMf("recommendation")} data-testid="close-maint-recommendation" />}</F>
              <F label="Motivo / política">{locked ? <LockedInput testid="close-maint-reason" /> : <Textarea rows={2} value={m.reason || ""} onChange={setMf("reason")} data-testid="close-maint-reason" />}</F>
              {locked && <p className="text-xs text-amber-400" data-testid="close-maint-locked-hint">Campos bloqueados: el mantenimiento se guardará como "{m.mode === "no_aplica" ? "No aplica" : "Por configurar"}", sin fecha, periodicidad, recomendación ni motivo.</p>}
              <p className="text-xs text-muted-foreground">La recomendación de mantenimiento no extiende la garantía.</p>
            </div>
            {charge && <CloseCharge info={prev.client_charge} c={charge} onChange={setCharge} />}
            {(prev.extra_charges || []).filter((x) => x.direct && extraCharges[x.service_id]).map((x) => (
              <div key={x.service_id} className="space-y-2" data-testid={`close-extra-charge-${x.service_id}`}>
                <h3 className="font-display text-sm font-semibold">Cobro del servicio adicional: {x.label}</h3>
                <CloseCharge info={x} c={extraCharges[x.service_id]} onChange={(v) => setExtraCharges((s) => ({ ...s, [x.service_id]: v }))} />
              </div>))}
            <F label="Notas de revisión administrativa"><Textarea rows={2} value={review} onChange={(e) => setReview(e.target.value)} data-testid="close-review-notes" /></F>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || !prev || (!wo.signature && noSig.trim().length < 3)} data-testid="close-confirm-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{wo.signature ? "Cerrar OS y generar PDF" : "Cerrar sin firma del cliente"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ReopenDialog = ({ wo, open, onOpenChange, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/work-orders/${wo.id}/reopen`, { reason });
      toast.success("OS reabierta");
      onOpenChange(false);
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reopen-dialog">
        <DialogHeader><DialogTitle className="font-display">Reabrir OS {wo.folio}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">La OS volverá a "Pendiente de cierre". El motivo quedará registrado en la auditoría.</p>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo de reapertura" data-testid="reopen-reason-input" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="reopen-confirm-button">Reabrir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
